import * as React from 'react';
import {
  CopyOutlined,
  KeyOutlined,
  MoreVerticalUniversal,
  PowerUniversal,
} from '@duxton-web/icons';
import {
  Avatar,
  Divider,
  notification,
  Popover,
  Typography,
} from '@duxton-web/ui';
import startCase from 'lodash/startCase';
import { Link } from 'react-router-dom';

import '@/features/layout/profile/index.css';

import { GoogleInfo } from '@/common/dto/Login';
import { SlackIcon } from '@/common/Icons/SlackIcon';
import { UserGuideIcon } from '@/common/Icons/UserGuideIcon';
import { isSSOLoginTenant } from '@/common/Tenants';
import { FEATURE_FLAGS, GRABX_SLACK_CHANNEL } from '@/constants';
import Service from '@/lib/App';
import { EmptyObject } from '@/types/common';
import { ConcedoToken } from '@/utils/concedoAuthHelper';
import { getToken } from '@/utils/localStorage';

interface CollapsedAvatarProps {
  domainFamily: string;
  collapsed: boolean;
  onLogout: () => void;
}

const Profile = ({
  collapsed,
  onLogout,
  domainFamily,
}: CollapsedAvatarProps) => {
  const concedoInfo: ConcedoToken | EmptyObject =
    Service.cache.getUserInfo() || {};
  const ssoUserInfo: GoogleInfo | EmptyObject =
    Service.cache.getSSOUserInfo() || {};

  const handleCopyToken = () => {
    const token = getToken();
    if (token) {
      navigator.clipboard.writeText(token);
      notification.success({ message: 'Token copied to clipboard' });
    }
  };

  const isServiceAuthZEnabled =
    Service.cache?.isFeatureFlagOn?.(FEATURE_FLAGS.SERVICE_AUTHZ) ?? false;
  const accessControlRoute = isServiceAuthZEnabled
    ? `/${domainFamily}/access-control`
    : `/${domainFamily}/user-roles`;

  const popoverContent = (
    <div className="collapsed-avatar-popoverContentContainer">
      <div onClick={onLogout} className="collapsed-avatar-popoverContentItem">
        <PowerUniversal size={20} />
        Logout
      </div>
      <Divider style={{ margin: 0 }} />
      <Link to={accessControlRoute} style={{ color: '#3D3D3D' }}>
        <div className="collapsed-avatar-popoverContentItem">
          <KeyOutlined size={20} />
          Access Control
        </div>
      </Link>
      <Divider style={{ margin: 0 }} />
      <div
        className="collapsed-avatar-popoverContentItem"
        onClick={handleCopyToken}
      >
        <CopyOutlined size={20} />
        Copy Authorization Token
      </div>
      <Link
        to="https://techdocs.grab.com/grabx/"
        target="_blank"
        style={{ color: '#3D3D3D' }}
      >
        <div className="collapsed-avatar-popoverContentItem">
          <UserGuideIcon width={14} height={16} />
          Documentation
        </div>
      </Link>
      <Link
        to={GRABX_SLACK_CHANNEL}
        target="_blank"
        style={{ color: '#3D3D3D' }}
      >
        <div className="collapsed-avatar-popoverContentItem">
          <SlackIcon size={16} />
          <span style={{ marginLeft: -2 }}>Slack Channel</span>
        </div>
      </Link>
    </div>
  );

  function getEmail(): string {
    let email: string;

    if (isSSOLoginTenant()) email = ssoUserInfo?.email;
    else email = concedoInfo?.email;

    if (!email) return '';

    return email;
  }

  function getDisplayName(): string {
    let emailWithoutPrefix = getEmail();
    const prefixesToRemove = ['ext.', 'ms.', 'ftt.', 'itn.'];
    prefixesToRemove.forEach((prefix) => {
      if (emailWithoutPrefix.startsWith(prefix)) {
        emailWithoutPrefix = emailWithoutPrefix.replace(prefix, '');
      }
    });

    return startCase(emailWithoutPrefix.split('@')[0].split('.').join(' '));
  }

  function getShortName(name = '') {
    if (!name) return '';

    const arr = name.split(/\.|\s/);
    let result = '';

    arr.forEach((s) => {
      result += `${s.charAt(0).toLocaleUpperCase()}`;
    });

    if (result.length > 2) {
      result = `${result.charAt(0)}${result.charAt(result.length - 1)}`;
    }

    return result;
  }

  return (
    <div className="collapsed-avatar-container">
      <Avatar
        style={{
          backgroundColor: '#EEF9F9',
          color: '#005339',
          fontSize: 14,
          flexShrink: 0,
        }}
        size={32}
      >
        {getShortName(getDisplayName())}
      </Avatar>
      {!collapsed && (
        <>
          <div className="collapsed-avatar-userInfoContainer">
            <Typography.Title
              data-testid="main-menu-profile-name"
              level={5}
              className="collapsed-avatar-userName"
              style={{ fontSize: 14, marginBottom: 0 }}
            >
              {getDisplayName()}
            </Typography.Title>
            <Typography.Text
              data-testid="main-menu-profile-email"
              type="secondary"
              className="collapsed-avatar-userEmail"
            >
              {getEmail()}
            </Typography.Text>
          </div>
          <Popover
            content={popoverContent}
            placement="topRight"
            trigger="click"
            arrow={false}
          >
            <MoreVerticalUniversal
              data-testid="main-menu-profile-more"
              size={20}
            />
          </Popover>
        </>
      )}
    </div>
  );
};

export default Profile;
